import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Check,
  Minus,
  Crown,
  Rocket,
  Building2,
  Star,
  Info,
} from 'lucide-react';

export const PLANS = [
  {
    id: 'head-start',
    name: 'Head Start',
    tagline: 'For schools getting started',
    icon: Rocket,
    accent: 'slate',
    monthlyPrice: 0,
    priceLabel: 'Free',
    schools: '1',
    coAdmins: 'Not available',
    subs: '8',
    postings: '12 / month',
    overages: 'Not available',
    features: [
      '1 school profile',
      'Up to 8 connected Subs',
      'Up to 12 postings per month',
      'Real-time alerts',
      'Basic analytics',
      'Full-feature access for Subs',
    ],
    footnotes: [],
  },
  {
    id: 'primary',
    name: 'Primary',
    tagline: 'Designed for small schools',
    icon: Building2,
    accent: 'emerald',
    monthlyPrice: 39,
    monthlyBillingPrice: 49,
    annualPrice: 468,
    priceLabel: '$39',
    annualLabel: '$468',
    schools: '1',
    coAdmins: 'Not available',
    subs: '20',
    postings: '30 / month',
    overages: '$5 per excess sub or posting',
    features: [
      '1 school profile with 1 School Admin',
      'Up to 20 connected Subs',
      'Up to 30 postings per month',
      'multi-day postings',
      'Posting completion log',
      'Overages: $5 per excess sub/posting',
      'Full analytics & messaging',
      'Position reminders',
    ],
    footnotes: [],
  },
  {
    id: 'intermediate',
    name: 'Intermediate',
    tagline: 'For schools with multiple divisions',
    icon: Star,
    accent: 'amber',
    monthlyPrice: 83,
    monthlyBillingPrice: 99,
    annualPrice: 996,
    priceLabel: '$83',
    annualLabel: '$996',
    schools: '3',
    coAdmins: '3',
    subs: 'Unlimited',
    postings: '100 / month',
    overages: '$2 per excess posting',
    badge: 'Most Popular',
    features: [
      'Up to 3 school profiles',
      'Up to 3 Co-Admin accounts',
      'Unlimited connected Subs',
      'Up to 100 postings per month',
      'Overages: $2 per excess posting',
      'Full analytics & messaging',
      'Adjustable position reminders',
      'Priority email support',
    ],
    footnotes: [],
  },
  {
    id: 'enterprise',
    name: 'Collegiate',
    tagline: 'For associations of schools',
    icon: Crown,
    accent: 'indigo',
    monthlyPrice: 208,
    monthlyBillingPrice: 249,
    annualPrice: 2496,
    priceLabel: '$208',
    annualLabel: '$2,496',
    schools: 'Up to 16',
    coAdmins: 'Unlimited',
    subs: 'Unlimited',
    postings: 'Unlimited',
    overages: 'No overages',
    features: [
      'Up to 16 school profiles**',
      'Unlimited Co-Admin accounts',
      'Unlimited connected Subs',
      'Unlimited postings',
      'No overage charges',
      'Includes all lower plan features',
      'Dedicated account manager',
      'Custom onboarding',
    ],
    footnotes: ['**contact us for larger needs'],
  },
];

export const PLAN_ID_MAP = {
  'head-start': 'head_start',
  primary: 'primary',
  intermediate: 'intermediate',
  enterprise: 'collegiate',
};

// Valid backend plan ids — used to validate the `plan` query param carried
// through the register/login -> billing flow so an unauthenticated visitor's
// plan choice survives authentication and is pre-selected on the billing page.
export const VALID_PLAN_IDS = Object.values(PLAN_ID_MAP);

// Reverse map: backend plan_id -> marketing plan id (the PLANS array is keyed
// by marketing ids like 'head-start' / 'enterprise', while the `plan` query
// param carries backend ids like 'head_start' / 'collegiate').
export const BACKEND_PLAN_ID_TO_MARKETING = Object.fromEntries(
  Object.entries(PLAN_ID_MAP).map(([marketingId, backendId]) => [backendId, marketingId])
);

// Billing cycles the checkout supports. Carried through the register/login ->
// billing flow as ?cycle=annual (monthly is the default and usually omitted).
export const VALID_BILLING_CYCLES = ['monthly', 'annual'];

export const isValidBillingCycle = (cycle) => VALID_BILLING_CYCLES.includes(cycle);

const accentStyles = {
  slate: {
    ring: 'ring-slate-200',
    icon: 'bg-slate-100 text-slate-700',
    button: 'bg-slate-900 hover:bg-slate-800 text-white',
  },
  emerald: {
    ring: 'ring-emerald-200',
    icon: 'bg-emerald-100 text-emerald-700',
    button: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  },
  amber: {
    ring: 'ring-amber-300 ring-2 shadow-lg shadow-amber-100',
    icon: 'bg-amber-100 text-amber-700',
    button: 'bg-amber-500 hover:bg-amber-600 text-white',
  },
  indigo: {
    ring: 'ring-indigo-200',
    icon: 'bg-indigo-100 text-indigo-700',
    button: 'bg-indigo-600 hover:bg-indigo-700 text-white',
  },
};

export default function PricingSection({
  isAuthenticated = false,
  onSelectPlan = null,
  preselectedPlanId = null,
  preselectedBillingCycle = 'monthly',
}) {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState(
    isValidBillingCycle(preselectedBillingCycle) ? preselectedBillingCycle : 'monthly'
  );

  const handleSelectPlan = (plan) => {
    if (onSelectPlan) {
      onSelectPlan(PLAN_ID_MAP[plan.id], plan, billingCycle);
      return;
    }

    if (isAuthenticated) {
      navigate('/employer');
    } else {
      // Carry the chosen plan (and billing cycle) through registration so the
      // billing page can pre-select both once the visitor has authenticated.
      navigate(
        `/register?role=employer&plan=${PLAN_ID_MAP[plan.id]}` +
          (billingCycle === 'annual' ? '&cycle=annual' : '')
      );
    }
  };

  return (
    <div className="space-y-8" data-testid="pricing-section">
      {/* Heading */}
      <div className="text-left">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Manrope']">
          Pricing & Plans
        </h2>
        <p className="mt-3 text-base sm:text-lg text-slate-600 max-w-2xl">
          Prices expressed in USD.
        </p>
      </div>

      {/* Billing cycle toggle — same pill style as the school Subscription page.
          Monthly shows the pay-monthly price; Annual shows the discounted
          effective monthly price with the annual total. */}
      <div className="flex justify-center" data-testid="pricing-cycle-toggle">
        <div className="bg-slate-100 rounded-lg p-1 inline-flex">
          <button
            type="button"
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="pricing-monthly-toggle"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center ${
              billingCycle === 'annual'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="pricing-annual-toggle"
          >
            Annual
            <Badge className="ml-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              Save up to 20%
            </Badge>
          </button>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const styles = accentStyles[plan.accent];
          const isPopular = !!plan.badge;
          const isAnnual = billingCycle === 'annual';
          const isFree = plan.monthlyPrice === 0;
          const isPreselected = preselectedPlanId === plan.id;
          // Monthly mode shows the pay-monthly price ($49/mo); annual mode shows
          // the discounted effective monthly rate ($39/mo) with the annual total.
          const displayPrice = isFree
            ? plan.priceLabel
            : isAnnual
              ? plan.priceLabel
              : `$${plan.monthlyBillingPrice}`;
          const annualSavingsPct = isFree
            ? 0
            : Math.round((1 - plan.monthlyPrice / plan.monthlyBillingPrice) * 100);

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col rounded-2xl border-slate-200 bg-white ring-1 ${
                styles.ring
              } ${isPopular ? 'lg:-translate-y-2' : ''} ${
                isPreselected ? 'ring-2 ring-emerald-500 border-emerald-500' : ''
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge
                    className="bg-amber-500 hover:bg-amber-500 text-white px-3 py-1 shadow"
                    data-testid="pricing-most-popular-badge"
                  >
                    <Star className="h-3 w-3 mr-1" />
                    {plan.badge}
                  </Badge>
                </div>
              )}
              {isPreselected && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge
                    className="bg-emerald-600 hover:bg-emerald-600 text-white px-3 py-1 shadow"
                    data-testid={`pricing-preselected-badge-${plan.id}`}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Your Selection
                  </Badge>
                </div>
              )}

              <CardContent className="p-6 flex flex-col flex-1">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 ${styles.icon}`}>
                  <Icon className="h-6 w-6" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 font-['Manrope']">{plan.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>

                <div className="mt-5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-slate-900 tracking-tight">
                      {displayPrice}
                    </span>
                    {!isFree && <span className="text-sm text-slate-500">/mo USD</span>}
                  </div>
                  {!isFree && isAnnual && (
                    <p
                      className="text-sm text-emerald-600 mt-1"
                      data-testid={`pricing-annual-note-${plan.id}`}
                    >
                      ${plan.annualPrice.toLocaleString('en-US')}/year (save {annualSavingsPct}%)
                    </p>
                  )}
                  {!isFree && !isAnnual && (
                    <p className="text-sm text-slate-400 mt-1">billed monthly</p>
                  )}
                </div>

                <ul className="mt-5 space-y-2 flex-1">
                  {plan.features.map((f) => {
                    // Overage costs aren't billable yet — mark that bullet gray + "Coming Soon"
                    // instead of styling it as an already-active feature.
                    const isOverage = f.startsWith('Overages:');
                    return (
                      <li
                        key={f}
                        className={`flex items-start gap-2 text-sm ${isOverage ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        <Check
                          className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isOverage ? 'text-slate-300' : 'text-emerald-600'}`}
                        />
                        <span className="flex items-center gap-1.5 flex-wrap">
                          {f}
                          {isOverage && (
                            <Badge
                              variant="secondary"
                              className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-[10px] font-semibold px-1.5 py-0"
                              data-testid="pricing-overage-coming-soon-badge"
                            >
                              Coming Soon
                            </Badge>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {plan.footnotes && plan.footnotes.length > 0 && (
                  <div className="mt-3 space-y-0.5">
                    {plan.footnotes.map((note) => (
                      <p key={note} className="text-xs text-slate-500 italic">
                        {note}
                      </p>
                    ))}
                  </div>
                )}

                {/* Head Start is the default free plan every account gets when
                    not on a paid subscription — it can't be "selected", so show
                    a static label instead of a checkout button. */}
                {plan.id === 'head-start' ? (
                  <div
                    className="mt-6 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-center text-sm font-medium text-slate-500"
                    data-testid="pricing-included-label"
                  >
                    Included with every account
                  </div>
                ) : (
                  <Button
                    className={`mt-6 w-full ${styles.button}`}
                    onClick={() => handleSelectPlan(plan)}
                    data-testid={`pricing-select-${plan.id}-btn`}
                  >
                    {isPreselected ? 'Continue with This Plan' : 'Select Plan'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Comparison table */}
      <Card className="border-slate-200 rounded-2xl overflow-hidden" data-testid="pricing-comparison-table-card">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h3 className="text-lg font-semibold text-slate-900 font-['Manrope']">Plan Comparison</h3>
          <p className="text-sm text-slate-500">Quick reference of limits & overages</p>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="pricing-comparison-table">
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold text-slate-700">Feature</TableHead>
                {PLANS.map((p) => (
                  <TableHead key={p.id} className="text-center font-semibold text-slate-700">
                    {p.name}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <ComparisonRow
                label="Price (monthly billing)"
                values={PLANS.map((p) =>
                  p.monthlyPrice === 0 ? 'Free' : `$${p.monthlyBillingPrice}/mo`,
                )}
              />
              <ComparisonRow
                label="Price (annual billing)"
                values={PLANS.map((p) =>
                  p.monthlyPrice === 0 ? 'Free' : `${p.annualLabel}/yr`,
                )}
              />
              <ComparisonRow label="Schools" values={PLANS.map((p) => p.schools)} />
              <ComparisonRow label="Co-Admin Accounts" values={PLANS.map((p) => p.coAdmins)} />
              <ComparisonRow label="Subs" values={PLANS.map((p) => p.subs)} />
              <ComparisonRow label="Postings" values={PLANS.map((p) => p.postings)} />
              <ComparisonRow label="Overages" values={PLANS.map((p) => p.overages)} />
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Footnote */}
      <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
        <div className="flex flex-col text-sm text-slate-600">
          <p>Express Sub Inc. is a Canadian owned Delaware corporation.</p>
          <p>
            Questions? Email{' '}
            <a href="mailto:support@expresssub.com" className="text-emerald-600 hover:underline">
              support@expresssub.com
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function ComparisonRow({ label, values }) {
  // Overage costs aren't billable yet — the Overages row grays out and badges any value
  // that isn't already one of the "no cost" states ('Not available', 'No overages').
  const isOverageRow = label === 'Overages';

  return (
    <TableRow>
      <TableCell className="font-medium text-slate-700">{label}</TableCell>
      {values.map((v, i) => {
        const isUnavailable = v === 'Not available';
        const isComingSoon = isOverageRow && !isUnavailable && v !== 'No overages';
        return (
          <TableCell key={`${label}-${i}`} className="text-center text-sm text-slate-700">
            {isUnavailable ? (
              <span className="inline-flex items-center gap-1 text-slate-400">
                <Minus className="h-3 w-3" />
                {v}
              </span>
            ) : isComingSoon ? (
              <span className="inline-flex flex-col items-center gap-1 text-slate-400">
                <span>{v}</span>
                <Badge
                  variant="secondary"
                  className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-[10px] font-semibold px-1.5 py-0"
                  data-testid={`pricing-comparison-overage-coming-soon-badge-${i}`}
                >
                  Coming Soon
                </Badge>
              </span>
            ) : (
              v
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
